import React from 'react';
/*
 * Iconos leídos del Club_Sidebar de Figma (instancias lucide/*). Todo el menú usa
 * lucide para que no convivan dos familias con grosores distintos.
 * Dos se renombraron en lucide: bar-chart-2 → ChartNoAxesColumn, line-chart →
 * ChartLine. Y lucide/weed es `Cannabis`.
 * Las entradas que NO existen en el Figma de Dispensario (Productos, Workspace,
 * Ubicaciones, Actividad, Impuestos, Facturación, Superusuarios, Ops) llevan el
 * equivalente lucide más cercano, para mantener el conjunto coherente.
 */
import {
    LogIn, Cannabis, Coffee, ShoppingCart, Calendar, BotMessageSquare,
    UsersRound, QrCode, Warehouse, UserCog, ReceiptText, HandCoins, Store,
    ChartNoAxesColumn, ChartLine, Calculator, User, Settings, BadgeHelp,
    Package, FolderOpen, MapPin, ClipboardList, Percent, CreditCard,
    ShieldCheck, Terminal,
} from 'lucide-react';
import { getVenueTypeConfig, DEFAULT_VENUE_TYPE, VENUE_TYPES } from './venueTypes';

export const getDefaultVenuePath = (venueType = DEFAULT_VENUE_TYPE) => {
    if (venueType === VENUE_TYPES.BISTRO) {
        return '/carta';
    }

    if (
        venueType === VENUE_TYPES.CANNABIS_CLUB ||
        venueType === VENUE_TYPES.SOCIAL_CLUB
    ) {
        return '/control';
    }

    if (venueType === VENUE_TYPES.CBD_SHOP) {
        return '/dispenser?mode=productos';
    }

    return '/control';
};

export const getDashboardSidebarLinks = (venueType = DEFAULT_VENUE_TYPE) => {
    const venueConfig = getVenueTypeConfig(venueType);
    return [
    
    {
        key: 'control',
        // Club social: la pantalla es el centro de operaciones de la SALA
        // (entradas, atender, comandas, saldo). "Entradas" queda para cannabis.
        label: venueType === VENUE_TYPES.SOCIAL_CLUB ? 'Sala' : 'Entradas',
        path: `/control`,
        icon: <LogIn />,
        roles: ['owner', 'staff', 'superadmin'],
        enabled: venueConfig.showEntries,
    },

    {
        key: 'dispenser',
        label: 'Dispensario',
        path: `/dispenser`,
        icon: <Cannabis />,
        roles: ['owner', 'staff','superadmin'],
        enabled: venueConfig.showDispenser,
    },
    // El rediseño de Figma del dispensario vive en /dispensario (sin entrada de
    // menú: es WIP y no debe asomar a owners/staff). Cuando sustituya al de
    // producción, /dispenser apuntará ahí.
    {
        key: 'product',
        label: venueConfig.productLabel || 'Productos',
        path: venueType === VENUE_TYPES.BISTRO ? '/carta' : '/dispenser?mode=productos',
        icon: <Package />,
        roles: ['superadmin', 'owner', 'staff'],
        enabled: venueConfig.showProducts && venueType !== VENUE_TYPES.SOCIAL_CLUB,
    },
    {
        key: 'barra',
        label: 'Barra',
        path: `/barra`,
        icon: <Coffee />,
        roles: ['owner', 'staff', 'superadmin'],
        // Comandero (CRUO): cola compartida de comandas. Social club y bistro.
        enabled: venueType === VENUE_TYPES.SOCIAL_CLUB || venueType === VENUE_TYPES.BISTRO,
    },
    {
        key: 'carritos',
        label: 'Carritos',
        path: `/carritos`,
        icon: <ShoppingCart />,
        roles: ['superadmin', 'owner', 'staff'],
        enabled: venueConfig.showCarts,
        badge: 'cartsCount',   // sidebar resolves this dynamically
    },
    {
        key: 'calendar',
        label: 'Calendario',
        path: `/calendar`,
        icon: <Calendar />,
        roles: ['superadmin', 'owner', 'staff'],
        enabled: venueConfig.showCalendar,
    },
    { key: 'divider-1', divider: true },
    {
        key: 'asistente-ia',
        label: 'Asistente IA',
        path: `/asistente-ia`,
        icon: <BotMessageSquare />,
        roles: ['superadmin', 'owner', 'staff'],
    },
    {
        key: 'workspace',
        label: 'Workspace',
        path: `/workspace`,
        icon: <FolderOpen />,
        roles: ['superadmin', 'owner', 'staff'],
        enabled: false,
    },
    {
        key: 'customers',
        label: venueConfig.memberLabel,
        path: `/customers`,
        icon: <UsersRound />,
        enabled: venueConfig.showMembers,
    },
    {
        key: 'preinscripciones',
        label: 'Preinscripciones',
        path: `/preinscripciones`,
        icon: <QrCode />,
        roles: ['owner', 'staff', 'superadmin'],
        enabled: venueConfig.showMembers,
        badge: 'preregCount',   // special key — sidebar resolves this dynamically
    },
    {
        key: 'almacen',
        label: 'Almacen',
        path: `/almacen`,
        icon: <Warehouse />,
        roles: ['superadmin', 'owner', 'staff'],
    },
    {
        key: 'ubicaciones',
        label: 'Ubicaciones',
        path: `/ubicaciones`,
        icon: <MapPin />,
        roles: ['superadmin', 'owner', 'staff'],
    },
    {
        key: 'staff',
        label: 'Personal',
        path: `/staff`,
        icon: <UserCog />,
        roles: ['superadmin', 'owner','staff']
    },
    {
        key: 'cajas',
        label: 'Cajas',
        path: `/cajas`,
        icon: <ReceiptText />,
        roles: ['superadmin', 'owner', 'staff']
    },
    {
        key: 'pagos-terceros',
        label: 'Gastos y compras',
        path: `/pagos-terceros`,
        icon: <HandCoins />,
        roles: ['superadmin', 'owner', 'staff']
    },
    {
        key: 'estadisticas',
        label: 'Estadísticas',
        path: `/estadisticas`,
        icon: <ChartNoAxesColumn />,
        roles: ['superadmin', 'owner', 'staff']
    },
    {
        key: 'rentabilidad',
        label: 'Rentabilidad',
        path: `/rentabilidad`,
        icon: <ChartLine />,
        roles: ['superadmin', 'owner', 'staff']
    },
    {
        key: 'transactions',
        label: 'Contabilidad',
        path: `/transactions`,
        icon: <Calculator />,
        roles: ['superadmin', 'owner', 'staff']
    },
    {
        key: 'actividad',
        label: 'Actividad',
        path: `/actividad`,
        icon: <ClipboardList />,
        roles: ['superadmin', 'owner', 'staff']
    },
    {
        key: 'impuestos',
        label: 'IVA / Impuestos',
        path: `/impuestos`,
        icon: <Percent />,
        roles: ['superadmin', 'owner']
    },
    {
        key: 'facturacion',
        label: 'Mi Suscripción',
        path: `/facturacion`,
        icon: <CreditCard />,
        roles: ['owner'],
    },
    {
        key: 'superusers',
        label: 'Superusuarios',
        path: `/superusers`,
        icon: <ShieldCheck />,
        roles: ['superadmin'],
    },
    {
        key: 'shops',
        label: 'Tiendas',
        path: `/shops`,
        icon: <Store />,
        roles: [ 'superadmin'],
    },
    {
        key: 'ops',
        label: 'Deploy Panel',
        path: `/ops`,
        icon: <Terminal />,
        roles: ['superadmin'],
    },
 

];
};

export const DASHBOARD_SIDEBAR_BOTTOM_LINKS = [
    {
        key: 'my_shops',
        label: 'Mis Tiendas',
        path: '/my_shops',
        icon: <Store />,
        roles: ['owner']
    },
    {
        key: 'profile',
        label: 'Perfil',
        path: '/profile',
        icon: <User />
    },
    {
        key: 'settings',
        label: 'Configuración',
        path: '/settings',
        icon: <Settings />
    },
    {
        key: 'support',
        label: 'Ayuda y Soporte',
        path: '/support',
        icon: <BadgeHelp />
    }
];
